import { TranslateService } from "@ngx-translate/core";

export enum AcaoPermissao {
    Autorizar = 1,
    AutorizarSolicitandoSenha = 2,
    BloquearOcultar = 3,
    BloquearMasSolicitarSenha = 4
}

// Ids de funcionalidade_sistema usados no WEB (módulos 1 - Web e 3 - Web/PDV)
export const FuncionalidadeSistemaId = {
    AcessarProduto: 21,
    AcessarRelatorioProduto: 22,
    AcessarCadastroProduto: 23,
    GravarProduto: 24,
    DefinirTributacaoEmLote: 28,
    AcessarNfce: 29,
    AcessarPdv: 30,
    AcessarRelatorioVendas: 31,
    AcessarRelatorioCaixa: 32,
    AcessarUsuarios: 33,
    IncluirEditarUsuario: 34,
    AcessarConfiguracoes: 35,
    AcessarDashboard: 36,
    ImportarProdutos: 37,
    // Tela da Ficha de Consumo: id 2, o mesmo do PDV (Web/PDV)
    AcessarFichaConsumo: 2,
    AcessarDashboardFichaConsumo: 38,
    AcessarRelatorioFichaConsumo: 39
} as const;

// Telas do menu, na ordem em que o usuário é levado quando a tela que tentou abrir está bloqueada.
// A NFC-e fica de fora porque tem outra condição (NFC-e ativa) além da permissão.
export const TelasDoMenu: { url: string; funcionalidadeId: number }[] = [
    { url: '/home', funcionalidadeId: FuncionalidadeSistemaId.AcessarDashboard },
    { url: '/product', funcionalidadeId: FuncionalidadeSistemaId.AcessarProduto },
    { url: '/pdv', funcionalidadeId: FuncionalidadeSistemaId.AcessarPdv },
    { url: '/sales/report', funcionalidadeId: FuncionalidadeSistemaId.AcessarRelatorioVendas },
    { url: '/sales/cashbox', funcionalidadeId: FuncionalidadeSistemaId.AcessarRelatorioCaixa },
    { url: '/sales/consumption-ticket', funcionalidadeId: FuncionalidadeSistemaId.AcessarFichaConsumo },
    { url: '/user', funcionalidadeId: FuncionalidadeSistemaId.AcessarUsuarios }
];

// Chave de tradução do nome de cada funcionalidade, usado nas mensagens e no modal
export const NomeFuncionalidade: Record<number, string> = {
    [FuncionalidadeSistemaId.AcessarProduto]: 'accessApproval.productName',
    [FuncionalidadeSistemaId.AcessarRelatorioProduto]: 'accessApproval.productReportName',
    [FuncionalidadeSistemaId.AcessarCadastroProduto]: 'accessApproval.productRegisterName',
    [FuncionalidadeSistemaId.GravarProduto]: 'accessApproval.productSaveName',
    [FuncionalidadeSistemaId.DefinirTributacaoEmLote]: 'accessApproval.setTaxationInBatchName',
    [FuncionalidadeSistemaId.AcessarNfce]: 'accessApproval.nfceName',
    [FuncionalidadeSistemaId.AcessarPdv]: 'accessApproval.pdvName',
    [FuncionalidadeSistemaId.AcessarRelatorioVendas]: 'accessApproval.salesReportName',
    [FuncionalidadeSistemaId.AcessarRelatorioCaixa]: 'accessApproval.cashboxReportName',
    [FuncionalidadeSistemaId.AcessarUsuarios]: 'accessApproval.usersName',
    [FuncionalidadeSistemaId.IncluirEditarUsuario]: 'accessApproval.userRegisterName',
    [FuncionalidadeSistemaId.AcessarConfiguracoes]: 'accessApproval.configurationsName',
    [FuncionalidadeSistemaId.AcessarDashboard]: 'accessApproval.dashboardName',
    [FuncionalidadeSistemaId.ImportarProdutos]: 'accessApproval.productImportName',
    [FuncionalidadeSistemaId.AcessarFichaConsumo]: 'accessApproval.consumptionTicketName',
    [FuncionalidadeSistemaId.AcessarDashboardFichaConsumo]: 'accessApproval.consumptionTicketDashboardName',
    [FuncionalidadeSistemaId.AcessarRelatorioFichaConsumo]: 'accessApproval.consumptionTicketReportName'
};

// Usado no "data" das rotas protegidas pelo PermissaoGuard
export interface PermissaoRotaData {
    funcionalidadeId: number;
}

export interface MinhaAcaoPermissao {
    funcionalidadeSistemaId: number;
    acao: AcaoPermissao;
    isAdministrator: boolean;
}

// Resposta de GET /v1/permissao/minhas: a ação do usuário em cada funcionalidade do WEB
export interface MinhasPermissoes {
    isAdministrator: boolean;
    permissoes: { funcionalidadeSistemaId: number; acao: AcaoPermissao }[];
}

export enum TipoAutorizacaoWeb {
    Gravacao = 1, // uso único: vale para uma só requisição
    Leitura = 2   // vale enquanto a tela estiver aberta
}

// Resposta de POST /v1/autorizacao/validar
export interface TokenAutorizacao {
    token: string;
    tipo: TipoAutorizacaoWeb;
    expiresAt: string;
}

// Cabeçalho que leva o token de autorização às rotas protegidas da API
export const RequerPermissaoHeader = 'X-Autorizacao-Token';

// Rotas da API que exigem o token de autorização (cabeçalho X-Autorizacao-Token) quando a ação é 2 ou 4.
// Precisa acompanhar os [RequerPermissao] da API.
export const RotasProtegidasPorToken: { metodo: string; rota: RegExp; funcionalidadeId: number }[] = [
    { metodo: 'POST', rota: /^\/v1\/product$/, funcionalidadeId: FuncionalidadeSistemaId.GravarProduto },
    { metodo: 'PUT', rota: /^\/v1\/product\/\d+$/, funcionalidadeId: FuncionalidadeSistemaId.GravarProduto },
    { metodo: 'DELETE', rota: /^\/v1\/product\/\d+$/, funcionalidadeId: FuncionalidadeSistemaId.GravarProduto },
    { metodo: 'PUT', rota: /^\/v1\/product\/set-tax-ibs-cbs$/, funcionalidadeId: FuncionalidadeSistemaId.DefinirTributacaoEmLote },
    { metodo: 'POST', rota: /^\/v1\/product\/importation\/\d+\/confirm$/, funcionalidadeId: FuncionalidadeSistemaId.ImportarProdutos },
    { metodo: 'POST', rota: /^\/v1\/user$/, funcionalidadeId: FuncionalidadeSistemaId.IncluirEditarUsuario },
    { metodo: 'PUT', rota: /^\/v1\/user\/\d+$/, funcionalidadeId: FuncionalidadeSistemaId.IncluirEditarUsuario },
    { metodo: 'GET', rota: /^\/v1\/user\/(all-users|\d+)$/, funcionalidadeId: FuncionalidadeSistemaId.AcessarUsuarios },
    { metodo: 'GET', rota: /^\/v1\/sales\/.+$/, funcionalidadeId: FuncionalidadeSistemaId.AcessarRelatorioVendas },
    { metodo: 'GET', rota: /^\/v1\/cashbox\/(summary-report|detailed-report)$/, funcionalidadeId: FuncionalidadeSistemaId.AcessarRelatorioCaixa },
    { metodo: 'GET', rota: /^\/v1\/sales-ticket\/dashboard\/.+$/, funcionalidadeId: FuncionalidadeSistemaId.AcessarDashboardFichaConsumo },
    { metodo: 'GET', rota: /^\/v1\/sales-ticket\/report$/, funcionalidadeId: FuncionalidadeSistemaId.AcessarRelatorioFichaConsumo },
    { metodo: 'GET', rota: /^\/v1\/download\/nfce(\/url)?$/, funcionalidadeId: FuncionalidadeSistemaId.AcessarNfce },
    { metodo: 'POST', rota: /^\/v1\/download\/nfce$/, funcionalidadeId: FuncionalidadeSistemaId.AcessarNfce }
];

// Funcionalidades de consulta: a autorização vale enquanto a tela estiver aberta. As demais são de gravação.
const FuncionalidadesDeLeitura: number[] = [
    FuncionalidadeSistemaId.AcessarNfce,
    FuncionalidadeSistemaId.AcessarRelatorioVendas,
    FuncionalidadeSistemaId.AcessarRelatorioCaixa,
    FuncionalidadeSistemaId.AcessarUsuarios,
    FuncionalidadeSistemaId.AcessarDashboardFichaConsumo,
    FuncionalidadeSistemaId.AcessarRelatorioFichaConsumo
];

export function tipoDaFuncionalidade(funcionalidadeId: number): TipoAutorizacaoWeb {
    return FuncionalidadesDeLeitura.includes(funcionalidadeId) ? TipoAutorizacaoWeb.Leitura : TipoAutorizacaoWeb.Gravacao;
}

// Funcionalidade cuja rota exige o token (as demais só controlam tela ou botão no Angular)
export function funcionalidadeDaRota(metodo: string, rota: string): number | undefined {
    return RotasProtegidasPorToken.find(item => item.metodo === metodo.toUpperCase() && item.rota.test(rota))?.funcionalidadeId;
}

export function temRotaProtegida(funcionalidadeId: number): boolean {
    return RotasProtegidasPorToken.some(item => item.funcionalidadeId === funcionalidadeId);
}

// Código (messageCode) das recusas da autorização de acesso devolvidas pela API.
// Precisa acompanhar CodigoAutorizacaoWebEnum da API.
export enum CodigoAutorizacaoWeb {
    FuncionalidadeInvalida = 4001,
    InformeLoginOuAdministrador = 4002,
    CredencialInvalida = 4011,
    SemPermissao = 4031,
    AutorizacaoExpirada = 4032,
    AcessoNegadoMesmoUsuario = 4033,
    UsuarioSemAcessoParaAutorizar = 4034,
    UsuarioInativo = 4035,
    TentativasEsgotadas = 4291,
    AguardeNovaTentativa = 4292,
    OperacaoNaoConcluida = 5001
}

// Código da API -> chave de tradução (pt.json / en.json)
const ChaveDaMensagemPorCodigo: Record<number, string> = {
    [CodigoAutorizacaoWeb.FuncionalidadeInvalida]: 'accessApproval.invalidFeature',
    [CodigoAutorizacaoWeb.InformeLoginOuAdministrador]: 'accessApproval.informLoginOrAdmin',
    [CodigoAutorizacaoWeb.CredencialInvalida]: 'accessApproval.invalidCredentials',
    [CodigoAutorizacaoWeb.SemPermissao]: 'accessApproval.noPermissionAction',
    [CodigoAutorizacaoWeb.AutorizacaoExpirada]: 'accessApproval.authorizationExpired',
    [CodigoAutorizacaoWeb.AcessoNegadoMesmoUsuario]: 'accessApproval.sameUserDenied',
    [CodigoAutorizacaoWeb.UsuarioSemAcessoParaAutorizar]: 'accessApproval.authorizerWithoutPermission',
    [CodigoAutorizacaoWeb.UsuarioInativo]: 'accessApproval.inactiveUser',
    [CodigoAutorizacaoWeb.TentativasEsgotadas]: 'accessApproval.attemptsExhausted',
    [CodigoAutorizacaoWeb.AguardeNovaTentativa]: 'accessApproval.waitBeforeRetry',
    [CodigoAutorizacaoWeb.OperacaoNaoConcluida]: 'accessApproval.operationNotCompleted'
};

// Texto traduzido de um código da API; undefined se não for um código de autorização.
// "nome" é a funcionalidade, usada na mensagem de "autorizado sem acesso".
export function mensagemDoCodigoDeAutorizacao(translate: TranslateService, codigo?: number, nome?: string): string | undefined {
    const chave = codigo !== undefined ? ChaveDaMensagemPorCodigo[codigo] : undefined;

    return chave ? translate.instant(chave, { name: nome }) : undefined;
}
